/**
 * Pricing Management Page - Gestão de Preços e Fornecedores
 * 
 * Diretiva Executiva P1 & P2:
 * - Base de Custo para Vendas (FIFO > Último Custo > Melhor Fornecedor)
 * - Gestão de Preços por Fornecedor (histórico de preços)
 * - Margem mínima obrigatória (20%)
 * - Alertas de margem e quedas de preço
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { ResizableTableHandle } from '@/components/common/ResizableTableHandle';
import { useAuth } from '@/hooks/useAuth';
import { useResizableTableColumns } from '@/hooks/useResizableTableColumns';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useLanguage } from '@/i18n';
import { getStoredAuthToken } from '@/lib/authStorage';
import {
  Building2, Package, TrendingDown, AlertTriangle,
  Plus, Search, RefreshCw, History, Percent,
  ArrowDown, CheckCircle, XCircle, Eye, FileText,
  Calendar, BarChart3, Download, Upload, CheckSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRICING_SUPPLIERS_WIDTHS = {
  selector: 52,
  code: 118,
  name: 220,
  nuit: 136,
  contact: 164,
  phone: 146,
  terms: 132,
  status: 110,
  actions: 128,
};
const PRICING_COMPARISON_WIDTHS = {
  product: 220,
  sku: 132,
  best: 112,
  worst: 112,
  variance: 112,
  supplier: 184,
  count: 108,
  history: 96,
};
const PRICING_ALERTS_WIDTHS = {
  product: 220,
  cost: 112,
  price: 112,
  margin: 108,
  minimum: 118,
  alert: 118,
};

const parseResponse = async (response) => {
  let raw = '';
  try {
    raw = await response.text();
  } catch {
    try {
      raw = await response.clone().text();
    } catch {
      raw = '';
    }
  }
  let payload = {};
  try {
    payload = raw ? JSON.parse(raw) : {};
  } catch {
    payload = {};
  }

  if (!response.ok) {
    throw new Error(payload?.detail || payload?.message || raw || 'Error');
  }

  return payload;
};

const api = {
  get: async (url) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return parseResponse(res);
  },
  post: async (url, data) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return parseResponse(res);
  },
  postForm: async (url, formData) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    return parseResponse(res);
  },
  put: async (url, data) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      method: 'PUT',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return parseResponse(res);
  },
  download: async (url, filename) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Error');
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }
};

export default function PricingPage() {
  const { t } = useLanguage();
  const { hasPermission, hasRole } = useAuth();
  const { getColumnStyle: getPricingSupplierColumnStyle, startColumnResize: startPricingSupplierColumnResize } = useResizableTableColumns({
    storageKey: 'cg-pricing-suppliers-columns',
    defaultWidths: PRICING_SUPPLIERS_WIDTHS,
    minWidth: 72,
  });
  const { getColumnStyle: getPricingComparisonColumnStyle, startColumnResize: startPricingComparisonColumnResize } = useResizableTableColumns({
    storageKey: 'cg-pricing-comparison-columns',
    defaultWidths: PRICING_COMPARISON_WIDTHS,
    minWidth: 72,
  });
  const { getColumnStyle: getPricingAlertsColumnStyle, startColumnResize: startPricingAlertsColumnResize } = useResizableTableColumns({
    storageKey: 'cg-pricing-alerts-columns',
    defaultWidths: PRICING_ALERTS_WIDTHS,
    minWidth: 72,
  });
  const isAdmin = hasRole(['admin']);
  const [activeTab, setActiveTab] = useState('suppliers');
  const [suppliers, setSuppliers] = useState([]);
  const [marginAlerts, setMarginAlerts] = useState({ alerts: [], total_alerts: 0 });
  const [supplierComparison, setSupplierComparison] = useState({ comparisons: [], total_products: 0 });
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);

  // Dialogs
  const [createSupplierDialog, setCreateSupplierDialog] = useState(false);
  const [priceHistoryDialog, setPriceHistoryDialog] = useState({ open: false, productId: null, data: null });
  const [supplierViewDialog, setSupplierViewDialog] = useState({ open: false, data: null });
  const [importDialog, setImportDialog] = useState({ open: false, file: null, upsert: true, importing: false });
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ open: false, confirmation: '', reason: '' });

  // Forms
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    nuit: '',
    contact_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    payment_terms: '30 dias'
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [suppliersRes, alertsRes, comparisonRes, weeklyRes] = await Promise.all([
        api.get(`/api/pricing/suppliers?include_inactive=true&include_deleted=${showDeleted}`),
        api.get('/api/pricing/analytics/margin-alerts'),
        api.get('/api/pricing/reports/supplier-comparison'),
        api.get('/api/pricing/reports/weekly-prices')
      ]);
      setSuppliers(suppliersRes || []);
      setMarginAlerts(alertsRes || { alerts: [], total_alerts: 0 });
      setSupplierComparison(comparisonRes || { comparisons: [], total_products: 0 });
      setWeeklyReport(weeklyRes || null);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [showDeleted]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetSupplierSelection = useCallback(() => {
    setSelectedSupplierIds([]);
  }, []);

  const refreshPricingData = useCallback(async ({ clearSelection = false } = {}) => {
    if (clearSelection) {
      resetSupplierSelection();
    }
    await loadData();
  }, [loadData, resetSupplierSelection]);

  useEffect(() => {
    resetSupplierSelection();
  }, [resetSupplierSelection, searchTerm, showDeleted]);

  const handleCreateSupplier = async () => {
    if (!supplierForm.name) {
      toast.error('Nome do fornecedor obrigatório');
      return;
    }
    try {
      await api.post('/api/pricing/suppliers', supplierForm);
      toast.success('Fornecedor criado com sucesso');
      setCreateSupplierDialog(false);
      setSupplierForm({
        name: '',
        nuit: '',
        contact_name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        payment_terms: '30 dias'
      });
      await refreshPricingData();
    } catch (error) {
      toast.error('Erro ao criar fornecedor: ' + error.message);
    }
  };

  const handleViewPriceHistory = async (productId) => {
    try {
      const data = await api.get(`/api/pricing/supplier-prices/${productId}/history`);
      setPriceHistoryDialog({ open: true, productId, data });
    } catch (error) {
      toast.error('Erro ao carregar histórico: ' + error.message);
    }
  };

  const handleViewSupplier = async (supplierId) => {
    try {
      const data = await api.get(`/api/pricing/suppliers/${supplierId}`);
      setSupplierViewDialog({ open: true, data });
    } catch (error) {
      toast.error('Erro ao carregar fornecedor');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await api.download('/api/pricing/suppliers/template.xlsx', 'fornecedores_import_template.xlsx');
      toast.success('Template de fornecedores descarregado');
    } catch (error) {
      toast.error('Erro ao descarregar template: ' + error.message);
    }
  };

  const handleImportSuppliers = async () => {
    if (!importDialog.file) {
      toast.error('Seleccione um ficheiro .xlsx');
      return;
    }
    try {
      setImportDialog((prev) => ({ ...prev, importing: true }));
      const formData = new FormData();
      formData.append('file', importDialog.file);
      const result = await api.postForm(`/api/pricing/suppliers/import?upsert_by_code_or_name=${importDialog.upsert}`, formData);
      toast.success(`Importação concluída: ${result.created} criados, ${result.updated} actualizados`);
      if (result.total_errors > 0) {
        toast.warning(`Foram detectados ${result.total_errors} erros no ficheiro`);
      }
      setImportDialog({ open: false, file: null, upsert: true, importing: false });
      await refreshPricingData();
    } catch (error) {
      toast.error('Erro na importação: ' + error.message);
      setImportDialog((prev) => ({ ...prev, importing: false }));
    }
  };

  const toggleSupplierSelection = (supplierId, checked) => {
    setSelectedSupplierIds((prev) => {
      if (checked) {
        return prev.includes(supplierId) ? prev : [...prev, supplierId];
      }
      return prev.filter((id) => id !== supplierId);
    });
  };

  const handleRestoreSingleSupplier = async (supplierId) => {
    try {
      const result = await api.post('/api/pricing/suppliers/bulk-status', {
        ids: [supplierId],
        action: 'restore_soft',
        reason: 'Restauro individual via Gestão de Preços',
      });
      toast.success(result.message || 'Fornecedor restaurado');
      await refreshPricingData();
    } catch (error) {
      toast.error('Erro ao restaurar fornecedor: ' + error.message);
    }
  };

  const toggleSelectAllVisible = (checked) => {
    if (checked) {
      setSelectedSupplierIds(filteredSuppliers.map((supplier) => supplier.id));
      return;
    }
    setSelectedSupplierIds([]);
  };

  const handleBulkStatus = async (action) => {
    if (!selectedSupplierIds.length) {
      toast.error('Seleccione fornecedores para ação em massa');
      return;
    }
    try {
      const result = await api.post('/api/pricing/suppliers/bulk-status', {
        ids: selectedSupplierIds,
        action,
        reason: `Ação em massa: ${action}`,
      });
      toast.success(result.message || 'Ação em massa concluída');
      await refreshPricingData({ clearSelection: true });
    } catch (error) {
      toast.error('Erro na ação em massa: ' + error.message);
    }
  };

  const handleBulkHardDelete = async () => {
    if (!selectedSupplierIds.length) {
      toast.error('Seleccione fornecedores para hard delete');
      return;
    }
    try {
      const result = await api.post('/api/pricing/suppliers/hard-delete-bulk', {
        ids: selectedSupplierIds,
        confirmation: hardDeleteDialog.confirmation,
        reason: hardDeleteDialog.reason,
      });
      const blockedCount = (result.blocked || []).length;
      toast.success(`Hard delete concluído: ${result.deleted} removidos${blockedCount ? `, ${blockedCount} bloqueados` : ''}`);
      setHardDeleteDialog({ open: false, confirmation: '', reason: '' });
      await refreshPricingData({ clearSelection: true });
    } catch (error) {
      toast.error('Erro no hard delete em massa: ' + error.message);
    }
  };

  const handleExportSelectedSuppliers = () => {
    if (!selectedSupplierIds.length) {
      toast.error('Seleccione fornecedores para exportar');
      return;
    }

    const selected = suppliers.filter((supplier) => selectedSupplierIds.includes(supplier.id));
    if (!selected.length) {
      toast.error('Nenhum fornecedor seleccionado para exportação');
      return;
    }

    const headers = ['Código', 'Nome', 'NUIT', 'Contacto', 'Telefone', 'Email', 'Cidade', 'Prazo', 'Estado'];
    const lines = selected.map((supplier) => [
      supplier.code || '',
      supplier.name || '',
      supplier.nuit || '',
      supplier.contact_name || '-',
      supplier.phone || '-',
      supplier.email || '-',
      supplier.city || '-',
      supplier.payment_terms || '-',
      supplier.is_deleted ? 'Apagado' : (supplier.is_active ? 'Activo' : 'Inactivo'),
    ]);

    const csvContent = [headers, ...lines]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `pricing_fornecedores_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`${selected.length} fornecedores exportados`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-MZ', {
      style: 'currency',
      currency: 'MZN',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const filteredSuppliers = useMemo(() => suppliers.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.code?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [searchTerm, suppliers]);

  const allVisibleSelected = useMemo(
    () => filteredSuppliers.length > 0 && filteredSuppliers.every((supplier) => selectedSupplierIds.includes(supplier.id)),
    [filteredSuppliers, selectedSupplierIds]
  );
  const canRecoverSuppliers = hasPermission('suppliers.recover');

  return (
    <div className="space-y-4" data-testid="pricing-page">
      <ModulePageHero
        testId="pricing-page-hero"
        title={t('pricingPage.title')}
        description={t('pricingPage.description')}
        icon={Percent}
        badge="Pricing"
        compact
        stats={[
          { label: 'Fornecedores', value: suppliers.length, helper: `${filteredSuppliers.length} no filtro` },
          { label: 'Alertas', value: marginAlerts.total_alerts || 0, helper: `${marginAlerts.critical || 0} críticos` },
          { label: 'Comparações', value: supplierComparison.total_products || 0, helper: 'produtos comparados' },
          { label: 'Margem mínima', value: '20%', helper: 'regra activa' },
        ]}
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="h-8 px-3 text-[11px]" onClick={() => setCreateSupplierDialog(true)} data-testid="create-supplier-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('pricingPage.newSupplier')}
            </Button>
          </div>
        )}
      />

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="flex flex-wrap items-center gap-2 p-2.5 text-xs" data-testid="pricing-summary-compact-strip">
          <Badge variant="outline">{suppliers.filter(s => s.is_active).length} activos</Badge>
          <Badge variant="outline">Alertas: {marginAlerts.total_alerts}</Badge>
          <Badge variant="outline">Críticos: {marginAlerts.critical || 0}</Badge>
          <Badge variant="outline">Margem mínima: 20%</Badge>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm" data-testid="pricing-tabs-list">
          <TabsTrigger value="suppliers" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            {t('pricingPage.tabs.suppliers')}
          </TabsTrigger>
          <TabsTrigger value="comparison" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            {t('pricingPage.tabs.comparison')}
          </TabsTrigger>
          <TabsTrigger value="weekly" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            {t('pricingPage.tabs.weekly')}
          </TabsTrigger>
          <TabsTrigger value="alerts" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            {t('pricingPage.tabs.alerts')}
            {marginAlerts.total_alerts > 0 && (
              <Badge variant="destructive" className="ml-1">{marginAlerts.total_alerts}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* SUPPLIERS TAB */}
        <TabsContent value="suppliers">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="px-3 py-2.5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <CardTitle>Fornecedores</CardTitle>
                  <CardDescription>Lista operacional compacta</CardDescription>
                </div>
                <div className="flex flex-col gap-3 xl:items-end">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadTemplate}
                      data-testid="pricing-download-suppliers-template-button"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Template Excel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setImportDialog({ open: true, file: null, upsert: true, importing: false })}
                      data-testid="pricing-open-suppliers-import-dialog-button"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Importar Excel
                    </Button>
                    <Button
                      variant={showDeleted ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setShowDeleted((prev) => !prev)}
                      data-testid="pricing-toggle-supplier-deleted-button"
                    >
                      {showDeleted ? 'Ocultar apagados' : 'Mostrar apagados'}
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      disabled={!selectedSupplierIds.length}
                      onClick={() => handleBulkStatus('deactivate')}
                      data-testid="pricing-suppliers-bulk-deactivate-button"
                    >
                      Desativar
                    </Button>
                    <Button
                      variant="outline"
                      disabled={!selectedSupplierIds.length}
                      onClick={() => handleBulkStatus('activate')}
                      data-testid="pricing-suppliers-bulk-activate-button"
                    >
                      Activar
                    </Button>
                    <Button
                      variant="outline"
                      disabled={!selectedSupplierIds.length}
                      onClick={() => handleBulkStatus('restore_soft')}
                      data-testid="pricing-suppliers-bulk-restore-button"
                    >
                      Restaurar
                    </Button>
                    <Button
                      variant="outline"
                      disabled={!selectedSupplierIds.length}
                      onClick={handleExportSelectedSuppliers}
                      data-testid="pricing-suppliers-bulk-export-button"
                    >
                      Exportar
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="destructive"
                        disabled={!selectedSupplierIds.length}
                        onClick={() => setHardDeleteDialog({ open: true, confirmation: '', reason: '' })}
                        data-testid="pricing-open-suppliers-hard-delete-dialog-button"
                      >
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Hard Delete ({selectedSupplierIds.length})
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Pesquisar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="h-10 w-56 pl-9"
                      data-testid="pricing-suppliers-search-input"
                    />
                  </div>
                    <Button size="sm" variant="outline" onClick={loadData} data-testid="pricing-suppliers-refresh-button">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                </div>
              ) : filteredSuppliers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum fornecedor encontrado</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[520px]">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10 bg-white">
                      <tr className="border-b">
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('selector')}>
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                            data-testid="pricing-suppliers-select-all-checkbox"
                          />
                          <ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('selector', event)} testId="pricing-suppliers-column-resize-selector" />
                        </th>
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('code')}>Código<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('code', event)} testId="pricing-suppliers-column-resize-code" /></th>
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('name')}>Nome<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('name', event)} testId="pricing-suppliers-column-resize-name" /></th>
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('nuit')}>NUIT<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('nuit', event)} testId="pricing-suppliers-column-resize-nuit" /></th>
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('contact')}>Contacto<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('contact', event)} testId="pricing-suppliers-column-resize-contact" /></th>
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('phone')}>Telefone<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('phone', event)} testId="pricing-suppliers-column-resize-phone" /></th>
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('terms')}>Prazo Pagamento<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('terms', event)} testId="pricing-suppliers-column-resize-terms" /></th>
                        <th className="relative text-left py-3 px-4" style={getPricingSupplierColumnStyle('status')}>Estado<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('status', event)} testId="pricing-suppliers-column-resize-status" /></th>
                        <th className="relative text-right py-3 px-4" style={getPricingSupplierColumnStyle('actions')}>Acções<ResizableTableHandle onMouseDown={(event) => startPricingSupplierColumnResize('actions', event)} testId="pricing-suppliers-column-resize-actions" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSuppliers.map((supplier) => (
                        <tr key={supplier.id} className="border-b hover:bg-gray-50" data-testid={`pricing-supplier-row-${supplier.id}`}>
                          <td className="py-3 px-4" style={getPricingSupplierColumnStyle('selector')}>
                            <input
                              type="checkbox"
                              checked={selectedSupplierIds.includes(supplier.id)}
                              onChange={(e) => toggleSupplierSelection(supplier.id, e.target.checked)}
                              data-testid={`pricing-supplier-select-checkbox-${supplier.id}`}
                            />
                          </td>
                          <td className="py-3 px-4 font-mono text-sm" style={getPricingSupplierColumnStyle('code')}>{supplier.code}</td>
                          <td className="py-3 px-4 font-medium" style={getPricingSupplierColumnStyle('name')}>{supplier.name}</td>
                          <td className="py-3 px-4 text-sm" style={getPricingSupplierColumnStyle('nuit')}>{supplier.nuit || '-'}</td>
                          <td className="py-3 px-4 text-sm" style={getPricingSupplierColumnStyle('contact')}>{supplier.contact_name || '-'}</td>
                          <td className="py-3 px-4 text-sm" style={getPricingSupplierColumnStyle('phone')}>{supplier.phone || '-'}</td>
                          <td className="py-3 px-4 text-sm" style={getPricingSupplierColumnStyle('terms')}>{supplier.payment_terms || '-'}</td>
                          <td className="py-3 px-4" style={getPricingSupplierColumnStyle('status')}>
                            {supplier.is_deleted ? (
                              <Badge className="bg-red-100 text-red-800">Apagado</Badge>
                            ) : supplier.is_active ? (
                              <Badge className="bg-green-100 text-green-800">Activo</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-800">Inactivo</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right" style={getPricingSupplierColumnStyle('actions')}>
                            <UniversalCrudActions
                              entityLabel="pricing-supplier"
                              entityId={supplier.id}
                              isDeleted={supplier.is_deleted}
                              canView
                              canEdit={false}
                              canDelete={false}
                              canRecover={canRecoverSuppliers}
                              onView={() => handleViewSupplier(supplier.id)}
                              onRecover={() => handleRestoreSingleSupplier(supplier.id)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUPPLIER COMPARISON TAB */}
        <TabsContent value="comparison">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Comparação de Preços entre Fornecedores
              </CardTitle>
              <CardDescription>
                Identifique o melhor fornecedor por produto e oportunidades de redução de custos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {supplierComparison.comparisons?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Sem dados de comparação disponíveis</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {supplierComparison.total_products} produtos | {supplierComparison.products_with_multiple_suppliers} com múltiplos fornecedores
                    </p>
                  </div>
                  <div className="overflow-x-auto max-h-[520px]">
                    <table className="w-full">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b">
                          <th className="relative text-left py-3 px-4" style={getPricingComparisonColumnStyle('product')}>Produto<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('product', event)} testId="pricing-comparison-column-resize-product" /></th>
                          <th className="relative text-left py-3 px-4" style={getPricingComparisonColumnStyle('sku')}>SKU<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('sku', event)} testId="pricing-comparison-column-resize-sku" /></th>
                          <th className="relative text-right py-3 px-4" style={getPricingComparisonColumnStyle('best')}>Melhor Preço<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('best', event)} testId="pricing-comparison-column-resize-best" /></th>
                          <th className="relative text-right py-3 px-4" style={getPricingComparisonColumnStyle('worst')}>Pior Preço<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('worst', event)} testId="pricing-comparison-column-resize-worst" /></th>
                          <th className="relative text-right py-3 px-4" style={getPricingComparisonColumnStyle('variance')}>Variação<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('variance', event)} testId="pricing-comparison-column-resize-variance" /></th>
                          <th className="relative text-left py-3 px-4" style={getPricingComparisonColumnStyle('supplier')}>Melhor Fornecedor<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('supplier', event)} testId="pricing-comparison-column-resize-supplier" /></th>
                          <th className="relative text-center py-3 px-4" style={getPricingComparisonColumnStyle('count')}>Fornecedores<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('count', event)} testId="pricing-comparison-column-resize-count" /></th>
                          <th className="relative text-center py-3 px-4" style={getPricingComparisonColumnStyle('history')}>Histórico<ResizableTableHandle onMouseDown={(event) => startPricingComparisonColumnResize('history', event)} testId="pricing-comparison-column-resize-history" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierComparison.comparisons?.slice(0, 50).map((item, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium" style={getPricingComparisonColumnStyle('product')}>{item.product_name}</td>
                            <td className="py-3 px-4 text-sm font-mono" style={getPricingComparisonColumnStyle('sku')}>{item.product_sku || '-'}</td>
                            <td className="py-3 px-4 text-right font-bold text-green-600" style={getPricingComparisonColumnStyle('best')}>
                              {formatCurrency(item.best_price)}
                            </td>
                            <td className="py-3 px-4 text-right text-red-600" style={getPricingComparisonColumnStyle('worst')}>
                              {formatCurrency(item.worst_price)}
                            </td>
                            <td className="py-3 px-4 text-right" style={getPricingComparisonColumnStyle('variance')}>
                              {item.price_variance > 0 ? (
                                <Badge className={item.price_variance > 20 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                                  +{item.price_variance}%
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800">0%</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm" style={getPricingComparisonColumnStyle('supplier')}>{item.best_supplier}</td>
                            <td className="py-3 px-4 text-center" style={getPricingComparisonColumnStyle('count')}>
                              <Badge variant="outline">{item.supplier_count}</Badge>
                            </td>
                            <td className="py-3 px-4 text-center" style={getPricingComparisonColumnStyle('history')}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewPriceHistory(item.product_id)}
                                data-testid={`view-product-price-history-btn-${item.product_id}`}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* WEEKLY REPORT TAB */}
        <TabsContent value="weekly">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Relatório Semanal de Preços
                  </CardTitle>
                  <CardDescription>
                    Análise completa de custos, preços e margens
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      await api.post('/api/pricing/reports/generate-weekly', {});
                      toast.success('Relatório gerado com sucesso');
                      loadData();
                    } catch (error) {
                      toast.error('Erro ao gerar relatório');
                    }
                  }}
                  data-testid="generate-weekly-report-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Gerar Relatório Manual
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!weeklyReport ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Carregando relatório...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Produtos com Pricing</p>
                        <p className="text-2xl font-bold">{weeklyReport.summary?.products_with_pricing ?? weeklyReport.summary?.total_products}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Introduzidos no Inventário</p>
                        <p className="text-2xl font-bold">{weeklyReport.summary?.total_inventory_products ?? '-'}</p>
                      </CardContent>
                    </Card>
                    <Card className={(weeklyReport.summary?.products_missing_pricing_data || 0) > 0 ? 'border-amber-200' : ''}>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Sem Pricing Completo</p>
                        <p className={`text-2xl font-bold ${(weeklyReport.summary?.products_missing_pricing_data || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {weeklyReport.summary?.products_missing_pricing_data ?? 0}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Valor Total Custo</p>
                        <p className="text-xl font-bold">{formatCurrency(weeklyReport.summary?.total_cost_value)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Valor Total Venda</p>
                        <p className="text-xl font-bold">{formatCurrency(weeklyReport.summary?.total_sale_value)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Margem Média</p>
                        <p className="text-2xl font-bold text-green-600">{weeklyReport.summary?.average_margin_percent?.toFixed(1)}%</p>
                      </CardContent>
                    </Card>
                    <Card className={weeklyReport.summary?.alerts_count > 0 ? 'border-red-200' : ''}>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Alertas</p>
                        <p className={`text-2xl font-bold ${weeklyReport.summary?.alerts_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {weeklyReport.summary?.alerts_count}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Alerts List */}
                  {weeklyReport.alerts?.length > 0 && (
                    <div>
                      <h3 className="font-medium mb-3 flex items-center gap-2 text-red-600">
                        <AlertTriangle className="h-5 w-5" />
                        Produtos com Alertas de Margem ({weeklyReport.alerts.length})
                      </h3>
                      <div className="max-h-96 overflow-auto">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-white">
                            <tr className="border-b">
                              <th className="relative text-left py-2 px-3" style={getPricingAlertsColumnStyle('product')}>Produto<ResizableTableHandle onMouseDown={(event) => startPricingAlertsColumnResize('product', event)} testId="pricing-alerts-column-resize-product" /></th>
                              <th className="relative text-right py-2 px-3" style={getPricingAlertsColumnStyle('cost')}>Custo<ResizableTableHandle onMouseDown={(event) => startPricingAlertsColumnResize('cost', event)} testId="pricing-alerts-column-resize-cost" /></th>
                              <th className="relative text-right py-2 px-3" style={getPricingAlertsColumnStyle('price')}>Preço<ResizableTableHandle onMouseDown={(event) => startPricingAlertsColumnResize('price', event)} testId="pricing-alerts-column-resize-price" /></th>
                              <th className="relative text-right py-2 px-3" style={getPricingAlertsColumnStyle('margin')}>Margem<ResizableTableHandle onMouseDown={(event) => startPricingAlertsColumnResize('margin', event)} testId="pricing-alerts-column-resize-margin" /></th>
                              <th className="relative text-right py-2 px-3" style={getPricingAlertsColumnStyle('minimum')}>Preço Mínimo<ResizableTableHandle onMouseDown={(event) => startPricingAlertsColumnResize('minimum', event)} testId="pricing-alerts-column-resize-minimum" /></th>
                              <th className="relative text-center py-2 px-3" style={getPricingAlertsColumnStyle('alert')}>Alerta<ResizableTableHandle onMouseDown={(event) => startPricingAlertsColumnResize('alert', event)} testId="pricing-alerts-column-resize-alert" /></th>
                            </tr>
                          </thead>
                          <tbody>
                            {weeklyReport.alerts.map((item, idx) => (
                              <tr key={idx} className={`border-b ${item.alert === 'critical' ? 'bg-red-50' : item.alert === 'warning' ? 'bg-yellow-50' : ''}`}>
                                <td className="py-2 px-3" style={getPricingAlertsColumnStyle('product')}>
                                  <p className="font-medium">{item.name}</p>
                                  <p className="text-xs text-muted-foreground">{item.sku}</p>
                                </td>
                                <td className="py-2 px-3 text-right" style={getPricingAlertsColumnStyle('cost')}>{formatCurrency(item.cost_price)}</td>
                                <td className="py-2 px-3 text-right" style={getPricingAlertsColumnStyle('price')}>{formatCurrency(item.sale_price)}</td>
                                <td className="py-2 px-3 text-right" style={getPricingAlertsColumnStyle('margin')}>
                                  <span className={item.margin_percent < 0 ? 'text-red-600 font-bold' : item.margin_percent < 20 ? 'text-yellow-600' : ''}>
                                    {item.margin_percent?.toFixed(1)}%
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-right text-green-600" style={getPricingAlertsColumnStyle('minimum')}>{formatCurrency(item.minimum_price)}</td>
                                <td className="py-2 px-3 text-center" style={getPricingAlertsColumnStyle('alert')}>
                                  <Badge className={
                                    item.alert === 'critical' ? 'bg-red-100 text-red-800' :
                                    item.alert === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-blue-100 text-blue-800'
                                  }>
                                    {item.alert === 'critical' ? 'Crítico' : item.alert === 'warning' ? 'Aviso' : 'Revisar'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {(weeklyReport.missing_pricing_items?.length || 0) > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="weekly-pricing-missing-data-alert">
                      <h4 className="font-medium text-amber-800 mb-2">Produtos sem dados completos de pricing</h4>
                      <div className="text-sm text-amber-700 space-y-1">
                        <p>{weeklyReport.summary?.products_missing_pricing_data} produtos introduzidos no inventário ainda sem custo/preço completo.</p>
                        <p>Actualize custo e preço de venda para incluir estes produtos nos indicadores semanais.</p>
                      </div>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">Informações do Relatório</h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p>• Margem mínima obrigatória: <strong>{weeklyReport.summary?.minimum_margin_required}%</strong></p>
                      <p>• Taxa de IVA: <strong>{weeklyReport.summary?.vat_rate}%</strong></p>
                      <p>• Produtos com margem crítica (abaixo do custo) são bloqueados automaticamente nas vendas</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MARGIN ALERTS TAB */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle>Alertas de Margem</CardTitle>
              <CardDescription>
                Produtos com margem abaixo de 20% ou preço abaixo do custo
              </CardDescription>
            </CardHeader>
            <CardContent>
              {marginAlerts.alerts?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>Todos os produtos estão com margem adequada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {marginAlerts.alerts?.map((alert, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        alert.severity === 'critical' 
                          ? 'bg-red-50 border-red-200' 
                          : 'bg-yellow-50 border-yellow-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {alert.severity === 'critical' ? (
                            <XCircle className="h-5 w-5 text-red-600" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-yellow-600" />
                          )}
                          <div>
                            <p className="font-medium">{alert.product_name}</p>
                            <p className="text-sm text-muted-foreground">SKU: {alert.sku}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">
                            <span className="text-muted-foreground">Margem:</span>{' '}
                            <span className={`font-bold ${alert.margin_percent < 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                              {alert.margin_percent?.toFixed(1)}%
                            </span>
                          </p>
                          <p className="text-sm">
                            <span className="text-muted-foreground">Custo:</span>{' '}
                            {formatCurrency(alert.cost_price)}
                          </p>
                          <p className="text-sm">
                            <span className="text-muted-foreground">Preço:</span>{' '}
                            {formatCurrency(alert.unit_price)}
                          </p>
                          <p className="text-sm text-green-600 font-medium">
                            Preço Mínimo: {formatCurrency(alert.minimum_price)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Badge variant={alert.alert_type === 'below_cost' ? 'destructive' : 'warning'}>
                          {alert.alert_type === 'below_cost' ? 'Abaixo do Custo' : 'Margem Baixa'}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewPriceHistory(alert.product_id)}
                        >
                          <History className="h-3 w-3 mr-1" />
                          Histórico
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* CREATE SUPPLIER DIALOG */}
      <Dialog open={createSupplierDialog} onOpenChange={setCreateSupplierDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Fornecedor</DialogTitle>
            <DialogDescription>Registar novo fornecedor no sistema</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={supplierForm.name}
                onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                placeholder="Nome do fornecedor"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>NUIT</Label>
                <Input
                  value={supplierForm.nuit}
                  onChange={(e) => setSupplierForm({ ...supplierForm, nuit: e.target.value })}
                  placeholder="000000000"
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                  placeholder="+258 84..."
                />
              </div>
            </div>
            <div>
              <Label>Contacto</Label>
              <Input
                value={supplierForm.contact_name}
                onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })}
                placeholder="Nome do contacto"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={supplierForm.email}
                onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                placeholder="email@fornecedor.com"
              />
            </div>
            <div>
              <Label>Morada</Label>
              <Input
                value={supplierForm.address}
                onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                placeholder="Endereço"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cidade</Label>
                <Input
                  value={supplierForm.city}
                  onChange={(e) => setSupplierForm({ ...supplierForm, city: e.target.value })}
                  placeholder="Maputo"
                />
              </div>
              <div>
                <Label>Prazo Pagamento</Label>
                <Select
                  value={supplierForm.payment_terms}
                  onValueChange={(v) => setSupplierForm({ ...supplierForm, payment_terms: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="À vista">À vista</SelectItem>
                    <SelectItem value="15 dias">15 dias</SelectItem>
                    <SelectItem value="30 dias">30 dias</SelectItem>
                    <SelectItem value="45 dias">45 dias</SelectItem>
                    <SelectItem value="60 dias">60 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateSupplierDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateSupplier} data-testid="confirm-create-supplier-btn">
              <Plus className="h-4 w-4 mr-2" />
              Criar Fornecedor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW SUPPLIER DIALOG */}
      <Dialog
        open={supplierViewDialog.open}
        onOpenChange={(open) => !open && setSupplierViewDialog({ open: false, data: null })}
      >
        <DialogContent className="max-w-xl" data-testid="pricing-supplier-view-dialog">
          <DialogHeader>
            <DialogTitle>Fornecedor</DialogTitle>
            <DialogDescription>{supplierViewDialog.data?.name}</DialogDescription>
          </DialogHeader>

          {supplierViewDialog.data && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase text-muted-foreground">Código</p>
                <p className="font-semibold">{supplierViewDialog.data.code || '-'}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase text-muted-foreground">Produtos com preço</p>
                <p className="font-semibold">{supplierViewDialog.data.stats?.products_count || 0}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase text-muted-foreground">Última compra</p>
                <p className="font-semibold">{formatDate(supplierViewDialog.data.stats?.recent_purchases?.[0]?.created_at)}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase text-muted-foreground">Volume de compras</p>
                <p className="font-semibold">{formatCurrency(supplierViewDialog.data.stats?.total_received_amount || 0)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={importDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setImportDialog({ open: false, file: null, upsert: true, importing: false });
          }
        }}
      >
        <DialogContent className="max-w-lg" data-testid="pricing-suppliers-import-dialog">
          <DialogHeader>
            <DialogTitle>Importar Fornecedores por Excel</DialogTitle>
            <DialogDescription>
              Use o template oficial da Gestão de Preços para adicionar ou actualizar fornecedores em massa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ficheiro .xlsx</Label>
              <Input
                type="file"
                accept=".xlsx"
                onChange={(e) => setImportDialog((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                data-testid="pricing-suppliers-import-file-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="pricing-suppliers-import-upsert-checkbox"
                type="checkbox"
                checked={importDialog.upsert}
                onChange={(e) => setImportDialog((prev) => ({ ...prev, upsert: e.target.checked }))}
                data-testid="pricing-suppliers-import-upsert-checkbox"
              />
              <Label htmlFor="pricing-suppliers-import-upsert-checkbox">Actualizar por código/nome quando já existir</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportDialog({ open: false, file: null, upsert: true, importing: false })}
              data-testid="pricing-suppliers-import-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleImportSuppliers}
              disabled={importDialog.importing}
              data-testid="pricing-suppliers-import-confirm-button"
            >
              {importDialog.importing ? 'A importar...' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={hardDeleteDialog.open} onOpenChange={(open) => !open && setHardDeleteDialog({ open: false, confirmation: '', reason: '' })}>
        <AlertDialogContent data-testid="pricing-suppliers-hard-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hard Delete em Massa (Gestão de Preços)</AlertDialogTitle>
            <AlertDialogDescription>
              Acção irreversível. Os fornecedores seleccionados serão removidos definitivamente se não tiverem dependências activas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm">Seleccionados: <strong data-testid="pricing-suppliers-hard-delete-selected-count">{selectedSupplierIds.length}</strong></p>
            <div>
              <Label>Confirmação *</Label>
              <Input
                value={hardDeleteDialog.confirmation}
                onChange={(e) => setHardDeleteDialog((prev) => ({ ...prev, confirmation: e.target.value }))}
                placeholder="Digite: APAGAR DEFINITIVAMENTE"
                data-testid="pricing-suppliers-hard-delete-confirmation-input"
              />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Textarea
                value={hardDeleteDialog.reason}
                onChange={(e) => setHardDeleteDialog((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Registe o motivo da remoção permanente"
                rows={3}
                data-testid="pricing-suppliers-hard-delete-reason-input"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pricing-suppliers-hard-delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkHardDelete}
              className="bg-red-700 hover:bg-red-800"
              data-testid="pricing-suppliers-hard-delete-confirm-button"
            >
              Hard Delete Definitivo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PRICE HISTORY DIALOG */}
      <Dialog open={priceHistoryDialog.open} onOpenChange={(open) => !open && setPriceHistoryDialog({ open: false, productId: null, data: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Histórico de Preços</DialogTitle>
            <DialogDescription>
              {priceHistoryDialog.data?.product?.name}
            </DialogDescription>
          </DialogHeader>
          {priceHistoryDialog.data && (
            <div className="space-y-4">
              {/* Current Prices */}
              <div>
                <h4 className="font-medium mb-2">Preços Actuais por Fornecedor</h4>
                {priceHistoryDialog.data.current_prices?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem preços registados</p>
                ) : (
                  <div className="space-y-2">
                    {priceHistoryDialog.data.current_prices?.map((price, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{price.supplier_name}</p>
                          <p className="text-xs text-muted-foreground">{price.supplier_code}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(price.unit_cost)}</p>
                          {idx === 0 && <Badge className="bg-green-100 text-green-800">Melhor Preço</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Price Drops */}
              {priceHistoryDialog.data.price_drops?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-green-600" />
                    Quedas de Preço (últimos 30 dias)
                  </h4>
                  <div className="space-y-2">
                    {priceHistoryDialog.data.price_drops.map((drop, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="font-medium">{drop.supplier_name}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-sm line-through text-muted-foreground">
                            {formatCurrency(drop.old_price)}
                          </span>
                          <ArrowDown className="h-4 w-4 text-green-600" />
                          <span className="font-bold text-green-600">
                            {formatCurrency(drop.new_price)}
                          </span>
                          <Badge className="bg-green-100 text-green-800">
                            -{drop.drop_percent}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* History Log */}
              {priceHistoryDialog.data.price_history?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Histórico de Alterações</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {priceHistoryDialog.data.price_history.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                        <span className="text-muted-foreground">{formatDate(entry.timestamp)}</span>
                        <span>
                          {formatCurrency(entry.old_cost)} → {formatCurrency(entry.new_cost)}
                          <span className={`ml-2 ${entry.change_percent > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ({entry.change_percent > 0 ? '+' : ''}{entry.change_percent?.toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceHistoryDialog({ open: false, productId: null, data: null })}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
